/**
 * @voltxampere/spice — SPICE numeric value parsing + emission
 *
 * Berkeley SPICE3 supports a fixed set of multiplier suffixes
 * appended to numeric literals:
 *
 *   T   = 1e12
 *   G   = 1e9
 *   MEG = 1e6   (case-insensitive — 'meg' / 'Meg' / 'MEG')
 *   K   = 1e3
 *   M   = 1e-3  (CRITICAL: 'M' alone = milli, NOT mega)
 *   U   = 1e-6  (Unicode 'μ' also accepted)
 *   N   = 1e-9
 *   P   = 1e-12
 *   F   = 1e-15
 *
 * Suffix matching is case-insensitive but length-greedy: `1MEG`
 * must resolve to 1e6 even though `M` alone is 1e-3. Any trailing
 * non-digit characters after the suffix are ignored (SPICE allows
 * `R1 N1 N2 4.7K Ohm` — the `Ohm` is human-readable noise).
 *
 * Emission picks the shortest canonical suffix that yields a tidy
 * number: 1000 → "1k", 4700 → "4.7k", 1e6 → "1MEG", 1e-6 → "1u".
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const MULTIPLIER_TABLE = [
    // Order matters — longer prefixes (MEG, MIL) first so the lexer
    // bites them before the single-letter `M` sneaks in front.
    ["MEG", 1e6],
    ["MIL", 25.4e-6], // electrical length, almost never used; accept for parity
    ["T", 1e12],
    ["G", 1e9],
    ["K", 1e3],
    ["M", 1e-3],
    ["U", 1e-6],
    ["N", 1e-9],
    ["P", 1e-12],
    ["F", 1e-15],
];
/** Greek mu maps to ASCII U for parsing (ngspice-compatible). */
const UNICODE_MU = "μ";
/**
 * Parse a SPICE numeric literal. Returns null on shape mismatch
 * (caller decides whether to raise).
 */
export function parseSpiceNumber(raw) {
    if (raw.length === 0)
        return null;
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return null;
    // Normalize Unicode μ → U so the suffix matcher catches it.
    const normalized = trimmed.replace(UNICODE_MU, "u");
    // Match an optional sign, the digit body (with optional decimal +
    // optional scientific exponent), then anything trailing — that's
    // the suffix we have to interpret.
    const numericMatch = /^([+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)(.*)$/.exec(normalized);
    if (!numericMatch)
        return null;
    const [, numberPart, tail] = numericMatch;
    if (numberPart === undefined || tail === undefined)
        return null;
    const base = Number.parseFloat(numberPart);
    if (!Number.isFinite(base))
        return null;
    if (tail.length === 0)
        return base;
    // Length-greedy suffix lookup — case-insensitive.
    const upper = tail.toUpperCase();
    for (const [suffix, factor] of MULTIPLIER_TABLE) {
        if (upper.startsWith(suffix)) {
            return base * factor;
        }
    }
    // No recognised suffix — accept the tail as a unit string (e.g.
    // `4.7K Ohm` → keep 4.7e3, drop the noise). Berkeley SPICE
    // tolerates this, so do we.
    return base;
}
/**
 * Render a numeric value back into a tidy SPICE string. Picks the
 * largest suffix that keeps the mantissa within `[0.1, 1000)`.
 */
export function formatSpiceValue(value) {
    if (!Number.isFinite(value))
        return "0";
    if (value === 0)
        return "0";
    const sign = value < 0 ? "-" : "";
    const abs = Math.abs(value);
    // Suffix preference (largest scale that yields a mantissa >= 1).
    const scales = [
        ["T", 1e12],
        ["G", 1e9],
        ["MEG", 1e6],
        ["k", 1e3],
        ["", 1],
        ["m", 1e-3],
        ["u", 1e-6],
        ["n", 1e-9],
        ["p", 1e-12],
        ["f", 1e-15],
    ];
    for (const [suffix, factor] of scales) {
        const mantissa = abs / factor;
        if (mantissa >= 1 && mantissa < 1000) {
            return `${sign}${trimMantissa(mantissa)}${suffix}`;
        }
    }
    // Fallback: scientific notation for absurdly large / small values.
    return `${sign}${abs.toExponential(3)}`;
}
function trimMantissa(value) {
    // 6 significant digits cap — beyond that we emit scientific.
    const fixed = value.toPrecision(6);
    // Strip trailing zeros after the decimal point.
    if (!fixed.includes("."))
        return fixed;
    return fixed.replace(/\.?0+$/, "");
}
//# sourceMappingURL=units.js.map